export type ExtractionSchemaRecord = Record<string, unknown>;
export type ExtractionSchemaType = 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array';
export type ExtractionValueType = Exclude<ExtractionSchemaType, 'array'>;

export type SchemaPathSegment = { kind: 'property'; key: string } | { kind: 'items' };
export type SchemaPath = SchemaPathSegment[];

// Groups start collapsed when a schema holds more leaf fields than this, so
// large schemas open as a scannable overview instead of a wall of rows.
export const COLLAPSE_THRESHOLD = 15;

export interface TypeOption {
    value: ExtractionValueType;
    label: string;
    listLabel: string;
    blurb: string;
}

export const TYPE_OPTIONS: TypeOption[] = [
    { value: 'string', label: 'Text', listLabel: 'List of Text', blurb: 'Names, IDs, or free text' },
    { value: 'number', label: 'Number', listLabel: 'List of Numbers', blurb: 'Amounts that can have decimals' },
    { value: 'integer', label: 'Whole number', listLabel: 'List of Whole numbers', blurb: 'Counts without decimals' },
    { value: 'boolean', label: 'Yes / no', listLabel: 'List of Yes / no', blurb: 'An answer that is yes or no' },
    { value: 'object', label: 'Group', listLabel: 'List of Groups', blurb: 'Related fields kept together' },
];

const SCHEMA_TYPES: ExtractionSchemaType[] = [...TYPE_OPTIONS.map((option) => option.value), 'array'];

const COMMON_SCHEMA_KEYS = new Set(['type', 'title', 'description']);
const OBJECT_SCHEMA_KEYS = new Set([
    ...Array.from(COMMON_SCHEMA_KEYS),
    'properties',
    'required',
    'additionalProperties',
]);
const ARRAY_SCHEMA_KEYS = new Set([...Array.from(COMMON_SCHEMA_KEYS), 'items']);

export function isRecord(value: unknown): value is ExtractionSchemaRecord {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function isSchemaType(value: unknown): value is ExtractionSchemaType {
    return SCHEMA_TYPES.includes(value as ExtractionSchemaType);
}

export function isValueType(value: ExtractionSchemaType): value is ExtractionValueType {
    return value !== 'array';
}

export function getValueTypeLabel(type: ExtractionValueType): string {
    return TYPE_OPTIONS.find((option) => option.value === type)?.label || type;
}

export function getValueTypeListLabel(type: ExtractionValueType): string {
    return TYPE_OPTIONS.find((option) => option.value === type)?.listLabel || `List of ${type}`;
}

export function getSchemaType(schema: ExtractionSchemaRecord): ExtractionSchemaType {
    return isSchemaType(schema.type) ? schema.type : 'string';
}

export function getSchemaProperties(schema: ExtractionSchemaRecord): ExtractionSchemaRecord {
    return isRecord(schema.properties) ? schema.properties : {};
}

export function getSchemaRequired(schema: ExtractionSchemaRecord): string[] {
    return Array.isArray(schema.required)
        ? schema.required.filter((value): value is string => typeof value === 'string')
        : [];
}

function getAllowedKeys(type: ExtractionSchemaType): Set<string> {
    if (type === 'object') {
        return OBJECT_SCHEMA_KEYS;
    }
    if (type === 'array') {
        return ARRAY_SCHEMA_KEYS;
    }
    return COMMON_SCHEMA_KEYS;
}

function formatSchemaPath(path: string): string {
    return path || 'schema';
}

export function getUnsupportedSchemaIssues(schema: unknown, path = ''): string[] {
    const location = formatSchemaPath(path);
    if (!isRecord(schema)) {
        return [`${location} must be a schema object`];
    }
    if (!isSchemaType(schema.type)) {
        return [`${location} uses unsupported type ${JSON.stringify(schema.type)}`];
    }
    if ('title' in schema && typeof schema.title !== 'string') {
        return [`${location}.title must be text`];
    }
    if ('description' in schema && typeof schema.description !== 'string') {
        return [`${location}.description must be text`];
    }

    const type = schema.type;
    const issues = Object.keys(schema)
        .filter((key) => !getAllowedKeys(type).has(key))
        .map((key) => `${location}.${key} is not supported by the visual editor`);

    if (type === 'object') {
        if ('additionalProperties' in schema && typeof schema.additionalProperties !== 'boolean') {
            issues.push(`${location}.additionalProperties must be true or false`);
        }
        if ('properties' in schema && !isRecord(schema.properties)) {
            issues.push(`${location}.properties must be an object`);
            return issues;
        }

        const properties = getSchemaProperties(schema);
        const propertyNames = new Set(Object.keys(properties));
        if ('required' in schema && !Array.isArray(schema.required)) {
            issues.push(`${location}.required must be an array`);
        } else if (Array.isArray(schema.required)) {
            const requiredNames = schema.required.filter((value): value is string => typeof value === 'string');
            if (requiredNames.length !== schema.required.length) {
                issues.push(`${location}.required must contain only property names`);
            }
            requiredNames.forEach((requiredName) => {
                if (!propertyNames.has(requiredName)) {
                    issues.push(`${location}.required references missing property ${requiredName}`);
                }
            });
        }

        Object.entries(properties).forEach(([key, property]) => {
            issues.push(...getUnsupportedSchemaIssues(property, path ? `${path}.${key}` : key));
        });
    }

    if (type === 'array') {
        if (!isRecord(schema.items)) {
            issues.push(`${location}.items must define one supported item type`);
        } else {
            issues.push(...getUnsupportedSchemaIssues(schema.items, path ? `${path}[]` : 'items'));
        }
    }

    return issues;
}

export function normalizeManagedSchema(schema: ExtractionSchemaRecord): ExtractionSchemaRecord {
    const type = getSchemaType(schema);
    const metadata = {
        ...(typeof schema.title === 'string' && schema.title.trim() ? { title: schema.title } : {}),
        ...(typeof schema.description === 'string' && schema.description.trim()
            ? { description: schema.description }
            : {}),
    };

    if (type === 'object') {
        const properties = Object.entries(getSchemaProperties(schema)).reduce((result, [key, property]) => {
            if (isRecord(property)) {
                result[key] = normalizeManagedSchema(property);
            }
            return result;
        }, {} as ExtractionSchemaRecord);
        const required = getSchemaRequired(schema).filter((key) =>
            Object.prototype.hasOwnProperty.call(properties, key),
        );
        return {
            type,
            ...metadata,
            additionalProperties: false,
            properties,
            ...(required.length ? { required } : {}),
        };
    }

    if (type === 'array') {
        return {
            type,
            ...metadata,
            items: normalizeManagedSchema(isRecord(schema.items) ? schema.items : { type: 'string' }),
        };
    }

    return { type, ...metadata };
}

export function buildSchemaForType(
    type: ExtractionSchemaType,
    currentSchema?: ExtractionSchemaRecord,
): ExtractionSchemaRecord {
    const metadata = {
        ...(typeof currentSchema?.title === 'string' ? { title: currentSchema.title } : {}),
        ...(typeof currentSchema?.description === 'string' ? { description: currentSchema.description } : {}),
    };
    if (type === 'object') {
        return { type, ...metadata, additionalProperties: false, properties: {} };
    }
    if (type === 'array') {
        return { type, ...metadata, items: { type: 'string' } };
    }
    return { type, ...metadata };
}

export function getSchemaAtPath(schema: ExtractionSchemaRecord, path: SchemaPath): ExtractionSchemaRecord {
    return path.reduce((current, segment) => {
        if (segment.kind === 'items') {
            return isRecord(current.items) ? current.items : { type: 'string' };
        }
        const property = getSchemaProperties(current)[segment.key];
        return isRecord(property) ? property : { type: 'string' };
    }, schema);
}

export function updateSchemaAtPath(
    schema: ExtractionSchemaRecord,
    path: SchemaPath,
    updater: (current: ExtractionSchemaRecord) => ExtractionSchemaRecord,
): ExtractionSchemaRecord {
    if (!path.length) {
        return updater(schema);
    }

    const [segment, ...remainingPath] = path;
    if (segment.kind === 'items') {
        const items = isRecord(schema.items) ? schema.items : { type: 'string' };
        return { ...schema, items: updateSchemaAtPath(items, remainingPath, updater) };
    }

    const properties = getSchemaProperties(schema);
    const propertyValue = properties[segment.key];
    const property: ExtractionSchemaRecord = isRecord(propertyValue) ? propertyValue : { type: 'string' };
    return {
        ...schema,
        properties: {
            ...properties,
            [segment.key]: updateSchemaAtPath(property, remainingPath, updater),
        },
    };
}

export function hasNestedConfiguration(schema: ExtractionSchemaRecord): boolean {
    if (getSchemaType(schema) === 'object') {
        return Object.keys(getSchemaProperties(schema)).length > 0;
    }
    return getSchemaType(schema) === 'array' && isRecord(schema.items);
}

export function getUniquePropertyKey(properties: ExtractionSchemaRecord): string {
    if (!Object.prototype.hasOwnProperty.call(properties, 'field')) {
        return 'field';
    }
    let suffix = 2;
    while (Object.prototype.hasOwnProperty.call(properties, `field${suffix}`)) {
        suffix += 1;
    }
    return `field${suffix}`;
}

export function titleToPropertyKey(title: string): string {
    const words = title
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .match(/[a-zA-Z0-9]+/g);
    if (!words?.length) {
        return '';
    }
    const [firstWord, ...remainingWords] = words;
    const camelCaseKey = [
        firstWord.toLowerCase(),
        ...remainingWords.map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`),
    ].join('');
    return /^\d/.test(camelCaseKey) ? `field${camelCaseKey}` : camelCaseKey;
}

export function getUniqueGeneratedKey(
    properties: ExtractionSchemaRecord,
    currentKey: string,
    title: string,
): string | null {
    const baseKey = titleToPropertyKey(title);
    if (!baseKey) {
        return null;
    }
    if (baseKey === currentKey || !Object.prototype.hasOwnProperty.call(properties, baseKey)) {
        return baseKey;
    }
    let suffix = 2;
    while (
        `${baseKey}${suffix}` !== currentKey &&
        Object.prototype.hasOwnProperty.call(properties, `${baseKey}${suffix}`)
    ) {
        suffix += 1;
    }
    return `${baseKey}${suffix}`;
}

export function getValueDefinition(schema: ExtractionSchemaRecord, path: SchemaPath) {
    let valueSchema = schema;
    let valuePath = path;
    let arrayDepth = 0;
    while (getSchemaType(valueSchema) === 'array' && isRecord(valueSchema.items)) {
        valueSchema = valueSchema.items;
        valuePath = [...valuePath, { kind: 'items' }];
        arrayDepth += 1;
    }
    const type = getSchemaType(valueSchema);
    return {
        arrayDepth,
        type: isValueType(type) ? type : ('string' as ExtractionValueType),
        valuePath,
        valueSchema,
    };
}

function getSchemaMetadata(schema: ExtractionSchemaRecord): ExtractionSchemaRecord {
    return {
        ...(typeof schema.title === 'string' ? { title: schema.title } : {}),
        ...(typeof schema.description === 'string' ? { description: schema.description } : {}),
    };
}

function withoutSchemaMetadata(schema: ExtractionSchemaRecord): ExtractionSchemaRecord {
    const { title: _title, description: _description, ...rest } = schema;
    return rest;
}

export function wrapSchemaAsArray(schema: ExtractionSchemaRecord): ExtractionSchemaRecord {
    return {
        type: 'array',
        ...getSchemaMetadata(schema),
        items: withoutSchemaMetadata(schema),
    };
}

export function unwrapSchemaArrays(schema: ExtractionSchemaRecord): ExtractionSchemaRecord {
    let valueSchema = schema;
    while (getSchemaType(valueSchema) === 'array' && isRecord(valueSchema.items)) {
        valueSchema = valueSchema.items;
    }
    return {
        ...withoutSchemaMetadata(valueSchema),
        ...getSchemaMetadata(schema),
    };
}

export function getPathId(path: SchemaPath, key?: string): string {
    const parts = path.map((segment) => (segment.kind === 'items' ? 'items' : segment.key));
    if (key) {
        parts.push(key);
    }
    return parts.join('-').replace(/[^a-zA-Z0-9_-]/g, '-');
}

// Collision-free identity for a schema path, used to key UI state (unlike
// getPathId, which is sanitized for data-test-id/aria usage and can collide).
export function serializePath(path: SchemaPath, key?: string): string {
    const segments = key ? [...path, { kind: 'property' as const, key }] : path;
    return JSON.stringify(segments);
}

export function deserializePath(serialized: string): SchemaPath {
    return JSON.parse(serialized) as SchemaPath;
}

export function moveProperty(
    objectSchema: ExtractionSchemaRecord,
    propertyKey: string,
    offset: -1 | 1,
): ExtractionSchemaRecord {
    const entries = Object.entries(getSchemaProperties(objectSchema));
    const index = entries.findIndex(([key]) => key === propertyKey);
    const targetIndex = index + offset;
    if (index < 0 || targetIndex < 0 || targetIndex >= entries.length) {
        return objectSchema;
    }
    const reordered = [...entries];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);
    return {
        ...objectSchema,
        properties: reordered.reduce((result, [key, value]) => {
            result[key] = value;
            return result;
        }, {} as ExtractionSchemaRecord),
    };
}

export function countNestedFields(propertySchema: ExtractionSchemaRecord): number {
    const { type, valueSchema } = getValueDefinition(propertySchema, []);
    if (type !== 'object') {
        return 0;
    }
    return Object.values(getSchemaProperties(valueSchema)).reduce<number>((count, property) => {
        if (!isRecord(property)) {
            return count;
        }
        return count + 1 + countNestedFields(property);
    }, 0);
}

export function countSchemaFields(root: ExtractionSchemaRecord): { fields: number; groups: number } {
    let fields = 0;
    let groups = 0;
    const visitObject = (objectSchema: ExtractionSchemaRecord) => {
        Object.values(getSchemaProperties(objectSchema)).forEach((property) => {
            if (!isRecord(property)) {
                return;
            }
            const { type, valueSchema } = getValueDefinition(property, []);
            if (type === 'object') {
                groups += 1;
                visitObject(valueSchema);
            } else {
                fields += 1;
            }
        });
    };
    visitObject(root);
    return { fields, groups };
}

export function describeSchemaSummary(root: ExtractionSchemaRecord): string {
    const { fields, groups } = countSchemaFields(root);
    const fieldsText = `${fields} ${fields === 1 ? 'field' : 'fields'}`;
    if (!groups) {
        return fieldsText;
    }
    return `${fieldsText} · ${groups} ${groups === 1 ? 'group' : 'groups'}`;
}

// Property paths (parent path + key) of every group-valued property, used to
// seed/expand the collapsible-group state.
export function collectGroupPaths(root: ExtractionSchemaRecord): SchemaPath[] {
    const result: SchemaPath[] = [];
    const visit = (objectSchema: ExtractionSchemaRecord, objectPath: SchemaPath) => {
        Object.entries(getSchemaProperties(objectSchema)).forEach(([key, property]) => {
            if (!isRecord(property)) {
                return;
            }
            const propertyPath: SchemaPath = [...objectPath, { kind: 'property', key }];
            const { type, valueSchema, valuePath } = getValueDefinition(property, propertyPath);
            if (type === 'object') {
                result.push(propertyPath);
                visit(valueSchema, valuePath);
            }
        });
    };
    visit(root, []);
    return result;
}

export function describeFieldSummary(propertySchema: ExtractionSchemaRecord): string {
    const { arrayDepth, type, valueSchema } = getValueDefinition(propertySchema, []);
    if (type === 'object') {
        const count = Object.keys(getSchemaProperties(valueSchema)).length;
        const label = arrayDepth > 0 ? 'List of Groups' : 'Group';
        return `${label} · ${count} ${count === 1 ? 'field' : 'fields'}`;
    }
    if (arrayDepth > 1) {
        return `List of lists · ${arrayDepth} levels`;
    }
    if (arrayDepth === 1) {
        return getValueTypeListLabel(type);
    }
    return getValueTypeLabel(type);
}

export function describeListHint(multiple: boolean, arrayDepth: number): string {
    if (arrayDepth > 1) {
        return `This field is a list of lists (${arrayDepth} levels). Turning this off keeps a single value.`;
    }
    return multiple
        ? 'On — every value found in the document will be captured.'
        : 'Off — only a single value will be captured.';
}

export interface FieldFilterResult {
    matchedPaths: Set<string>;
    visiblePaths: Set<string>;
    matchCount: number;
}

// Case-insensitive substring filter over key, field name, and description.
// Matches make every ancestor group visible; a matching group makes its whole
// subtree visible. Returns null for an empty query (no filtering).
export function filterSchemaFields(root: ExtractionSchemaRecord, query: string): FieldFilterResult | null {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
        return null;
    }
    const matchedPaths = new Set<string>();
    const visiblePaths = new Set<string>();
    const visit = (
        objectSchema: ExtractionSchemaRecord,
        objectPath: SchemaPath,
        ancestorPaths: string[],
        ancestorMatched: boolean,
    ) => {
        Object.entries(getSchemaProperties(objectSchema)).forEach(([key, property]) => {
            if (!isRecord(property)) {
                return;
            }
            const serializedPropertyPath = serializePath(objectPath, key);
            const title = typeof property.title === 'string' ? property.title : '';
            const description = typeof property.description === 'string' ? property.description : '';
            const matches = `${key}\n${title}\n${description}`.toLowerCase().includes(normalized);
            if (matches) {
                matchedPaths.add(serializedPropertyPath);
            }
            if (matches || ancestorMatched) {
                visiblePaths.add(serializedPropertyPath);
                ancestorPaths.forEach((ancestorPath) => visiblePaths.add(ancestorPath));
            }
            const propertyPath: SchemaPath = [...objectPath, { kind: 'property', key }];
            const { type, valueSchema, valuePath } = getValueDefinition(property, propertyPath);
            if (type === 'object') {
                visit(valueSchema, valuePath, [...ancestorPaths, serializedPropertyPath], ancestorMatched || matches);
            }
        });
    };
    visit(root, [], [], false);
    return { matchedPaths, visiblePaths, matchCount: matchedPaths.size };
}
